/**
 * One row in the file tree.
 *
 * Directories expand on click; files trigger the parent's onPickFile.
 * Right-click pops a context menu (copy path / pin — Phase 5+ for the
 * write-side actions). The chevron and trailing meta (size · mtime)
 * appear on row hover only, per the Notion playbook.
 *
 * Lazy load: a directory's children render only when `expanded` becomes
 * true. The parent's `useChildrenQuery` (in FileExplorerPanel) is what
 * actually fetches them — this component just paints rows.
 */
import type { FsTreeNode } from '@chaos/types/fs';
import { ChevronRight, FileIcon, FolderIcon } from 'lucide-react';
import * as React from 'react';
import { formatBytes, formatRelativeTime } from '../../lib/fs/viewer-kind';
import { cn } from '../../lib/utils';
import { useFileExplorerStore } from '../../stores/file-explorer-store';

interface FileTreeNodeProps {
  node: FsTreeNode;
  depth: number;
  isExpanded: boolean;
  isSelected: boolean;
  isLoading?: boolean;
  childrenContent?: React.ReactNode;
  onToggle: () => void;
  onSelect: () => void;
  onContextMenu?: (event: React.MouseEvent) => void;
}

export function FileTreeNode({
  node,
  depth,
  isExpanded,
  isSelected,
  isLoading = false,
  childrenContent,
  onToggle,
  onSelect,
  onContextMenu,
}: FileTreeNodeProps) {
  const isDir = node.kind === 'dir';
  // Slight left padding per depth so the tree feels indented without
  // losing density. 12px per level matches Notion's nested toggle
  // spacing.
  const indent = depth * 12;

  return (
    <li className="select-none">
      <button
        type="button"
        onClick={isDir ? onToggle : onSelect}
        onDoubleClick={isDir ? onSelect : undefined}
        onContextMenu={onContextMenu}
        aria-label={node.name}
        aria-expanded={isDir ? isExpanded : undefined}
        className={cn(
          'group/row flex w-full items-center gap-1 rounded px-1 py-0.5 text-left text-[13px] transition-colors',
          isSelected ? 'bg-foreground/5 text-foreground' : 'text-foreground hover:bg-foreground/5',
        )}
        style={{ paddingLeft: `${4 + indent}px` }}
      >
        {/* Chevron column — always 12px wide so directory/file rows align. */}
        <span className="flex h-4 w-3 shrink-0 items-center justify-center">
          {isDir ? (
            <ChevronRight
              aria-hidden
              className={cn(
                'h-3 w-3 text-muted-foreground transition-transform',
                isExpanded && 'rotate-90',
              )}
            />
          ) : null}
        </span>

        {/* Kind icon. */}
        {isDir ? (
          <FolderIcon className="h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden />
        ) : (
          <FileIcon className="h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden />
        )}

        {/* Name. */}
        <span className="min-w-0 flex-1 truncate" title={node.path}>
          {node.name}
        </span>

        {/* Trailing meta: size + mtime, only on hover. */}
        {!isDir && (
          <span className="hidden shrink-0 items-center gap-2 text-[11px] text-muted-foreground/80 group-hover/row:flex">
            {formatBytes(node.size_bytes ?? null) && (
              <span>{formatBytes(node.size_bytes ?? null)}</span>
            )}
            {formatRelativeTime(node.modified_at ?? null) && (
              <span>{formatRelativeTime(node.modified_at ?? null)}</span>
            )}
          </span>
        )}
      </button>

      {/* Children — rendered by parent so the data flow stays explicit. */}
      {isDir && isExpanded && (
        <ul className="flex flex-col">
          {isLoading && (
            <li
              className="px-2 py-0.5 text-[11px] text-muted-foreground"
              style={{ paddingLeft: `${4 + indent + 12}px` }}
            >
              Loading…
            </li>
          )}
          {childrenContent}
        </ul>
      )}
    </li>
  );
}

/**
 * Hover-revealed context menu — copy path / pin / unpin.
 * Phase 4 surfaces only the read-only actions; rename and delete are
 * stubs that we leave for Phase 5 once write-side scopes land.
 */
export interface ContextMenuPosition {
  x: number;
  y: number;
  path: string;
  isDir: boolean;
}

interface ContextMenuProps {
  pos: ContextMenuPosition;
  isPinned: boolean;
  onCopy: () => void;
  onPin: () => void;
  onUnpin: () => void;
  onClose: () => void;
}

export function FileTreeContextMenu({
  pos,
  isPinned,
  onCopy,
  onPin,
  onUnpin,
  onClose,
}: ContextMenuProps) {
  // Close on any click outside or on escape.
  React.useEffect(() => {
    const onDown = (e: MouseEvent): void => {
      const tgt = e.target as HTMLElement;
      if (!tgt.closest('[data-fs-context-menu]')) onClose();
    };
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('mousedown', onDown);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('mousedown', onDown);
      window.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  return (
    <div
      data-fs-context-menu
      role="menu"
      aria-label="File actions"
      className="fixed z-50 min-w-[160px] rounded-md border border-border bg-background py-1 shadow-md"
      style={{ left: pos.x, top: pos.y }}
    >
      <MenuItem onClick={onCopy}>Copy path</MenuItem>
      {isPinned ? (
        <MenuItem onClick={onUnpin}>Unpin</MenuItem>
      ) : (
        <MenuItem onClick={onPin}>Pin</MenuItem>
      )}
      <div className="my-1 h-px bg-border" />
      <MenuItem disabled>Rename</MenuItem>
      <MenuItem disabled className="text-muted-foreground">
        Delete
      </MenuItem>
    </div>
  );
}

function MenuItem({
  children,
  onClick,
  disabled = false,
  className,
}: {
  children: React.ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  className?: string;
}) {
  return (
    <button
      type="button"
      role="menuitem"
      disabled={disabled}
      onClick={() => {
        if (disabled) return;
        onClick?.();
      }}
      className={cn(
        'flex w-full items-center px-3 py-1.5 text-left text-[12px] transition-colors',
        disabled
          ? 'cursor-not-allowed text-muted-foreground/60'
          : 'text-foreground hover:bg-foreground/5',
        className,
      )}
    >
      {children}
    </button>
  );
}

/**
 * Returns a memoised list of strings stored in the user_pinned_paths
 * persistence layer so we can highlight pinned rows in the tree.
 * Phase 4 stores pins in a Zustand slice (see file-explorer-store);
 * server-side persistence arrives with /fs/pin in Phase 5.
 */
export function useIsPinned(path: string): boolean {
  const selectedPath = useFileExplorerStore((s) => s.selectedPath);
  // Currently pinning is a no-op surface — track local state only.
  return Boolean(selectedPath && false && path);
}
